import * as T from 'three';
import { getBestPlane } from '../dataAnalysis/plane';
import { getColorFromRamp, lerp } from '../helpers';

export class Line3D {
    constructor(scene, robotGroup, colors) {
        this.scene = scene;
        this.points = [];
        this.pointConfigs = [];
        this.mesh = null;
        this.added = false;
        this.exists = true;
        this.colors = colors;
        this.material = new T.MeshBasicMaterial({
            alphaToCoverage: true,
            vertexColors: true
        })
        this.robotGroup = robotGroup
        this.robot = robotGroup.robot;
        this.hasPoints = false;
        this.minTime = -1;
        this.maxVelocity = 0;
        this.maxTime = 0;
        this.lineWidth = 0.003;
        this.radialSegments = 10
        this.timeCycle = 1;

        this.ballMaterial = new T.MeshBasicMaterial({ 
            alphaToCoverage: true,
            color: 0xffffff 
        })
        this.lineStartBall = new T.Mesh(
            new T.SphereGeometry(this.lineWidth * 1.5),
            this.ballMaterial
        );
        this.lineEndBall = new T.Mesh(
            new T.SphereGeometry(this.lineWidth * 1.5),
            this.ballMaterial
        );
        
        this.path;
        this.colorRamp1 = [
            new T.Color("black")
        ]
        this.colors.forEach(c => {
            this.colorRamp1.push(c)
        })
        this.colorRamp1.push(new T.Color("white"))

        this.colorRamp2 = [
            new T.Color("Darkblue"),
            new T.Color("Green"),
            new T.Color("Yellow"),
            new T.Color("Red")
        ]
    }

    clearLine() {
        this.scene.remove(this.mesh);
        this.scene.remove(this.lineStartBall);
        this.scene.remove(this.lineEndBall);
        this.points = [];
        this.exists = false;
    }

    addPoint(newPos, time) {
        let vel = 0;
        let totalDist = 0;
        // let time = this.robotGroup.animationInterface.getCurrentTime()
        if (this.pointConfigs.length > 0) {
            let lastPoint = this.pointConfigs[this.pointConfigs.length - 1];
            let dist = lastPoint.position.distanceTo(newPos);
            vel = dist / (time - lastPoint.time);
            if (vel > this.maxVelocity) {
                this.maxVelocity = vel;
            }
            totalDist = dist + lastPoint.distance;  
        }
        if (time > this.maxTime) {
            this.maxTime = Number(time);
        }
        if(time < this.minTime || this.minTime == -1) {
            this.minTime = Number(time);
        }

        this.points.push(newPos);
        this.pointConfigs.push({
            jointConfigs: this.robotGroup.jointInterface.getJointConfigs(true),
            position: newPos,
            time: time,
            velocity: vel,
            distance: totalDist,
            camera: this.robotGroup.dataInterface.getCamera()
        });
    }

    /*
        // time, xPos, yPos, zPos, jointconfigs...
    */
    getPointsData() {
        let data = []
        let row1 = ["time", "x", "y", "z", "vel"]
        let configNames = this.robotGroup.jointInterface.getJointConfigsNames()
        // start at i = 13 to skip local and world position configs
        for(let i = 13; i < configNames.length; i++) {
            row1.push(configNames[i])
        }
        data.push(row1)
        this.pointConfigs.forEach(point => {
            let row = []
            row.push(point.time)
            row.push(point.position.x, point.position.y, point.position.z)
            row.push(point.velocity)
            point.jointConfigs.forEach(config => {
                row.push(config)
            })
            data.push(row)
        })
        return data
    }

    updateLine(newPos, time) {
        if (this.added) {
            this.scene.remove(this.mesh);
            this.scene.remove(this.lineStartBall);
            this.scene.remove(this.lineEndBall);
            this.added = false;
        }

        this.addPoint(newPos, time);

        if (this.points.length > 2) {
            this.path = new T.CatmullRomCurve3(this.points);
            let geometry = new T.TubeGeometry(this.path, this.points.length, this.lineWidth, this.radialSegments);
            this.mesh = new T.Mesh(geometry, this.material);
            // console.log("mesh created")
            this.lineStartBall.position.copy(this.points[0]);
            this.lineEndBall.position.copy(this.points[this.points.length-1]);
            this.recolor(0);
        }
        if (this.exists && !this.added && this.mesh) {
            this.scene.add(this.mesh);
            this.scene.add(this.lineStartBall);
            this.scene.add(this.lineEndBall);
            this.added = true;
        }
    }

    redrawLine() {
        if (this.added) {
            this.scene.remove(this.mesh);
            this.scene.remove(this.lineStartBall);
            this.scene.remove(this.lineEndBall);
            this.added = false;
        }
        if (this.points.length > 2) {
            this.path = new T.CatmullRomCurve3(this.points);
            let geometry = new T.TubeGeometry(this.path, this.points.length, this.lineWidth, this.radialSegments);
            this.mesh = new T.Mesh(geometry, this.material);
            this.lineStartBall = new T.Mesh(
                new T.SphereGeometry(this.lineWidth * 1.5),
                this.ballMaterial
            );
            this.lineEndBall = new T.Mesh(
                new T.SphereGeometry(this.lineWidth * 1.5),
                this.ballMaterial
            );
            this.lineStartBall.position.copy(this.points[0]);
            this.lineEndBall.position.copy(this.points[this.points.length-1]);
            this.recolor(0);
        }
        if (this.exists && !this.added && this.mesh) {
            this.scene.add(this.mesh);
            this.scene.add(this.lineStartBall);
            this.scene.add(this.lineEndBall);
            this.added = true;
        }
    }

    recolor(mode) {
        if (this.points.length < 2) {
            return;
        }
        let vertexColors = [];
        for (let i = 0; i < this.points.length; i++) {
            // time
            let t = 0;
            if (mode % 2 == 0) {
                t = (this.pointConfigs[i].time % this.timeCycle) / this.timeCycle;
            }
            // velocity
            else {
                t = this.pointConfigs[i].velocity / this.maxVelocity;
            }
            let ramp = mode < 2 ? this.colorRamp1 : this.colorRamp2
            vertexColors.push(getColorFromRamp(ramp, t));
        }

        let vertexColorIndex = 0;
        let distance = 0;

        let bufferVertexColors = new Float32Array((this.points.length + 1) * (this.radialSegments + 1) * 3);
        for (let i = 0; i < this.points.length + 1; i++) {
            let color;
            if(i < this.points.length - 1) {
                let t = 0;
                if(i > 0) {
                    distance += this.path.getPointAt(i/this.points.length).distanceTo(this.path.getPointAt((i-1)/this.points.length));
                }
                while(this.pointConfigs[vertexColorIndex].distance < distance && vertexColorIndex < this.points.length - 1) {
                    vertexColorIndex++;
                }
                if(vertexColorIndex != 0) {
                    vertexColorIndex--;
                }
                t = (distance - this.pointConfigs[vertexColorIndex].distance) / (this.pointConfigs[vertexColorIndex+1].distance - this.pointConfigs[vertexColorIndex].distance);
                t = isNaN(t) ? 0 : t;
                t = (t > 1) ? 1 : t;
                color = getColorFromRamp([vertexColors[vertexColorIndex], vertexColors[vertexColorIndex+1]], t)
            } 
            else {
                color = getColorFromRamp([vertexColors[vertexColorIndex], vertexColors[vertexColorIndex+1]], 1)
                // color.copy(vertexColors[vertexColors.length-1])
            }

            for (let k = 0; k < (this.radialSegments + 1); k++) {
                bufferVertexColors[(i * 3 * (this.radialSegments + 1)) + (k * 3) + 0] = color.r;
                bufferVertexColors[(i * 3 * (this.radialSegments + 1)) + (k * 3) + 1] = color.g;
                bufferVertexColors[(i * 3 * (this.radialSegments + 1)) + (k * 3) + 2] = color.b;
            }
        }
        this.lineStartBall.material.color.copy(vertexColors[0]);
        this.lineEndBall.material.color.copy(vertexColors[vertexColors.length-1]);
        this.mesh.geometry.setAttribute('color', new T.BufferAttribute(bufferVertexColors, 3));
    }

    // given a position, find the point on the line closest to it and set the robot to the what the position was at that time
    setRobotConfig(pos) {
        // let leastDist = 100;
        // let closestConfig;
        // this.pointConfigs.forEach(data => {
        //     let dist = pos.distanceTo(data.position);
        //     if (dist < leastDist) {
        //         leastDist = dist;
        //         closestConfig = data;
        //     }
        // })
        // if (closestConfig) {
        //     this.robotGroup.jointInterface.setJoints(closestConfig.jointConfigs)
        //     this.robotGroup.animationInterface.updateTime(closestConfig.time, true);
        // }
    }

    getMesh() {
        return this.mesh;
    }

    hideLine() {
        this.mesh.visible = false;
        this.lineStartBall.visible = false;
        this.lineEndBall.visible = false;
    }

    showLine() {
        this.mesh.visible = true;
        this.lineStartBall.visible = true;
        this.lineEndBall.visible = true;
    }

    setLineWidth(lineWidth) {
        this.lineWidth = lineWidth;
        this.redrawLine();
    }

    setTimeCycle(timeCycle) {
        if(timeCycle == -1) {
            this.timeCycle = this.maxTime - this.minTime
        } else {
            this.timeCycle = timeCycle;
        }
        this.redrawLine();
    }

    getRange() {
        return {
            minTime: this.minTime,
            maxTime: this.maxTime
        }
    }

    getColorRamp() {
        return this.colorRamp1
    }

    getCameraViewPoints() {
        let avgView = new T.Vector3()
        let avgFocus = new T.Vector3()

        let total = 0
        this.pointConfigs.forEach(point => {
            avgView.add(point.camera[0])
            avgFocus.add(point.camera[1])
            total++
        })

        avgView.divideScalar(total)
        avgFocus.divideScalar(total)

        return [avgView, avgFocus]
    }

    getLinePlane() {
        let planeData = getBestPlane(this.points)
        return planeData
    }
}
